import React from 'react';
import { Mail, Github, Linkedin, Phone, Download } from 'lucide-react'; // Added Download icon
import { portfolioContent } from './data';
// Define portfolio content data
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";


/**
 * Helper function to get a dynamic pixel-art icon for a given technology.
 * Uses placeholder images for a consistent retro aesthetic.
 * @param {string} tech - The technology name (e.g., "Python", "React").
 * @returns {JSX.Element} An <img> element with the corresponding pixel-art icon.
 */
const getTechIcon = (tech) => {
  const techName = String(tech || '').trim();
  const getInitials = () => {
    if (!techName) {
      return '?';
    }

    if (techName.length < 3) {
      return techName;
    }

    const words = techName
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .split(/[^A-Za-z0-9]+/)
      .filter(Boolean);

    if (words.length < 2) {
      return `${techName[0].toUpperCase()}${techName[1].toLowerCase()}`;
    }

    if (words.length === 2) {
      return words.map((word) => word[0].toUpperCase()).join('');
    }

    return [...words]
      .sort((a, b) => b.length - a.length)
      .slice(0, 2)
      .map((word) => word[0].toUpperCase())
      .join('');
  };
  const initials = encodeURIComponent(getInitials());

  return (
    <img
      src={`https://placehold.co/24x24/D0BC88/202020?text=${initials}`}
      alt={`${techName || 'Generic tech'} icon`}
      className="inline-block pixel-art"
    />
  );
};

/**
 * Main App component for the retro-themed developer portfolio.
 * It displays various sections: Header, About, Education, Experience, Skills, Achievements, and Contact.
 */
const App = () => {
  // Common styling for content sections for consistency
  const sectionClasses = "game-panel section-panel";
  const innerCardClasses = "game-card";
  const sectionTitleClasses = "section-title"; // Simplified for sprite-only titles

  return (
    // Main container with Press Start 2P font, retro background image
    <div
      className="game-shell"
      style={{
        backgroundImage: 'url("/1280px-HGSS_Kanto.png")', // Corrected direct image URL
      }}
    >
      {/* Header/Hero Section */}
      <header className="game-panel hero-panel">
        <div className="hero-copy">
          <span className="trainer-label">Trainer Card</span>
          <h1 className="hero-title">
            {portfolioContent.name}
          </h1>
          <p className="hero-subtitle">
            {portfolioContent.title}
          </p>
          {/* Display email address directly and make it non-clickable */}
          <p className="contact-line">
            <span className="inline-flex items-center">
              <Mail className="mr-2" size={18} /> {portfolioContent.email}
            </span>
          </p>
          <div className="action-row">
            <a
              href={portfolioContent.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="pixel-button icon-button blue-button"
              aria-label="LinkedIn Profile"
            >
              <Linkedin size={24} />
            </a>
            <a
              href={portfolioContent.github}
              target="_blank"
              rel="noopener noreferrer"
              className="pixel-button icon-button dark-button"
              aria-label="GitHub Profile"
            >
              <Github size={24} />
            </a>
            <a
              href={`mailto:${portfolioContent.email}`}
              className="pixel-button icon-button red-button"
              aria-label="Email"
            >
              <Mail size={24} />
            </a>
            {/* Download Resume Button */}
            <a
              href="/resume.pdf"
              className="pixel-button resume-button green-button"
              aria-label="Download Resume"
              target='_blank'
            >
              <Download className="mr-2" size={20} /> Resume
            </a>
          </div>
        </div>
        <div className="trainer-sprite-frame">
          {/* Trainer Sprite - Original orientation, without shake animation */}
          <img
            src="/Red_FRLG_Credits.png"
            alt="Pokémon Trainer Red"
            className="trainer-sprite pixel-art"
          />
        </div>
      </header>

      {/* About Section */}
      <section id="about" className={sectionClasses}>
        <div className="section-heading">
          {/* About Me Sprite updated to Bag Point Card Sprite */}
          <img
            src="/Bag_Point_Card_Sprite.png"
            alt="Point Card icon"
            className="section-sprite pixel-art"
          />
          <h2 className={sectionTitleClasses}>About Me</h2>
        </div>
        <p className="dialogue-text">
          {portfolioContent.about.intro}
        </p>
      </section>

      {/* Education Section */}
      <section id="education" className={sectionClasses}>
        <div className="section-heading">
          {/* Education Sprite updated to Bag Journal Sprite */}
          <img
            src="/Bag_Journal_Sprite.png"
            alt="Journal icon"
            className="section-sprite pixel-art"
          />
          <h2 className={sectionTitleClasses}>Education</h2>
        </div>
        <div className="card-grid two-column">
          {/* Dynamically render education items */}
          {portfolioContent.education.map((edu, index) => (
            <div key={index} className="game-card compact-card">
              <h3 className="card-title">{edu.level}</h3>
              <p className="card-strong">{edu.name}</p>
              {edu.degree && <p className="card-copy">{edu.degree}</p>}
              {edu.standard && <p className="card-copy">{edu.standard}</p>}
              <p className="card-copy">Score: {edu.score}</p>
              <p className="meta-text">{edu.dates}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className={sectionClasses}>
        <div className="section-heading">
          {/* Briefcase Sprite updated to Bag Exp. Share Sprite */}
          <img
            src="/Bag_Exp._Share_Sprite.png"
            alt="Experience Share Bag icon"
            className="section-sprite pixel-art"
          />
          <h2 className={sectionTitleClasses}>Experience</h2>
        </div>
        <div className="timeline-stack">
          {portfolioContent.experience.map((job, index) => (
            <div
              key={index}
              className={`${innerCardClasses} experience-card`}
            >
              <div className="job-meta">
                <span className="slot-number">{String(index + 1).padStart(2, '0')}</span>
                <h3 className="card-title">
                  {job.company}
                </h3>
                <p className="card-strong">{job.title}</p>
                <p className="meta-text">{job.dates}</p>
                <p className="meta-text">{job.location}</p>
              </div>
              <div className="job-body">
                <ul className="quest-list">
                  {job.description.map((point, i) => (
                    <li key={i}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {point}
                      </ReactMarkdown>
                    </li>
                  ))}
                </ul>
                <div className="chip-row">
                  {job.technologies.map((tech, i) => (
                    <span
                      key={i}
                      className="tech-chip"
                    >
                      {getTechIcon(tech)}
                      <span>{tech}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className={sectionClasses}>
        <div className="section-heading">
          {/* Skills Sprite updated to Bag Explorer Kit Sprite */}
          <img
            src="/Bag_Explorer_Kit_Sprite.png"
            alt="Explorer Kit icon"
            className="section-sprite pixel-art"
          />
          <h2 className={sectionTitleClasses}>Technical Skills</h2>
        </div>
        <div className="skills-grid">
          {portfolioContent.skills.map((skillGroup, groupIndex) => (
            <div
              key={groupIndex}
              className="game-card skill-card"
            >
              <div className="skill-card-header">
                <h3 className="card-title">
                  {skillGroup.group}
                </h3>
                <span className="count-badge">
                  {String(skillGroup.skillItems.length).padStart(2, '0')}
                </span>
              </div>
              <ul className="chip-row">
                {skillGroup.skillItems.map((skill, itemIndex) => (
                  <li
                    key={itemIndex}
                    className="tech-chip skill-chip"
                  >
                    {getTechIcon(skill)}
                    <span>{skill}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Achievements Section */}
      <section id="achievements" className={sectionClasses}>
        <div className="section-heading">
          {/* Award Sprite updated to Cool Ribbon Master Hoenn */}
          <img
            src="/Cool_Ribbon_Master_Hoenn.png"
            alt="Cool Ribbon icon"
            className="section-sprite pixel-art"
          />
          <h2 className={sectionTitleClasses}>Achievements</h2>
        </div>
        <div className="badge-case">
          {portfolioContent.achievements.map((achievement, index) => (
            <div
              key={index}
              className="badge-plaque"
            >
              <div className="badge-medallion">
                <span
                  className="sinnoh-badge pixel-art"
                  style={{
                    '--badge-index': index % 8,
                    backgroundImage: 'url("/sinnohba.png")',
                  }}
                  aria-hidden="true"
                />
                <span className="badge-number">
                  {String(index + 1).padStart(2, '0')}
                </span>
              </div>
              <div className="markdown-copy">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {achievement}
                </ReactMarkdown>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className={sectionClasses}>
        <div className="section-heading">
          {/* Communication Hub Sprite, now using the new Pokedex sprite */}
          <img
            src="/Key_Pok%C3%A9dex_m_Sprite.png"
            alt="Pokedex icon"
            className="section-sprite large-sprite pixel-art"
          />
          <h2 className={sectionTitleClasses}>Get in Touch</h2>
        </div>
        <p className="dialogue-text centered-text">
          I'm always open to new opportunities and collaborations. Feel free to reach out!
        </p>
        <div className="contact-actions">
          {/* Email button now links directly to mailto */}
          <a
            href={`mailto:${portfolioContent.email}`}
            className="pixel-button contact-button red-button"
            aria-label="Email"
          >
            <Mail className="mr-3" size={24} /> Email Me
          </a>

          <a
            href={portfolioContent.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="pixel-button contact-button gold-button"
          >
            <Linkedin className="mr-3" size={24} /> Connect on LinkedIn
          </a>
          {portfolioContent.phone && (
            <a
              href={`tel:${portfolioContent.phone}`}
              className="pixel-button contact-button green-button"
            >
              <Phone className="mr-3" size={24} /> Call Me
            </a>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="game-panel footer-panel">
        <p>&copy; {new Date().getFullYear()} {portfolioContent.name}. All rights reserved.</p>
        <p>Designed and Built by Gagan Sharma</p>
        <p>
          Pokémon sprites and background map from <a href="https://bulbapedia.bulbagarden.net" target="_blank" rel="noopener noreferrer">Bulbapedia</a>.
          Pokémon is &copy; Nintendo.
        </p>
      </footer>
    </div>
  );
};

export default App;
